import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import Spinner from "../../../spinner/spinner";
import ErrorScreen from "../../../error/error";
import { useLocation } from "react-router-dom";
import { useLanguageRegion } from "../../../providers/LanguageRegionContext";
import { useTranslation } from "react-i18next";

const CareSection = ({
  careData,
  prevClassName,
  nextClassName,
  cart,
  loadingProducts,
  errorProducts,
  handleAddToCart,
  handleRemoveFromCart,
  openAnalogsModal,
  openProductModal,
  errorText,
  isLoading,
  isError,
}) => {

  const location = useLocation();
  const isProfilePage = location.pathname.includes("profile");
  const { region } = useLanguageRegion();
  const { t } = useTranslation();
  const regionCode = (region?.code) || 'ru';

  // Region-based currency configuration (align with productCard/recs)
  const currencyMap = {
    ru: { currency: 'RUB', locale: 'ru-RU' },
    kk: { currency: 'KZT', locale: 'kk-KZ' },
    en: { currency: 'USD', locale: 'en-US' },
    es: { currency: 'EUR', locale: 'es-ES' },
    fr: { currency: 'EUR', locale: 'fr-FR' },
    it: { currency: 'EUR', locale: 'it-IT' },
    cs: { currency: 'CZK', locale: 'cs-CZ' },
    sl: { currency: 'EUR', locale: 'sk-SK' },
    uz: { currency: 'UZS', locale: 'uz-UZ' },
    vi: { currency: 'VND', locale: 'vi-VN' },
    ka: { currency: 'GEL', locale: 'ka-GE' },
    he: { currency: 'ILS', locale: 'he-IL' },
    zh: { currency: 'CNY', locale: 'zh-CN' },
    mex: { currency: 'MXN', locale: 'es-MX' },
    ro: { currency: 'RON', locale: 'ro-RO' },
    tr: { currency: 'TRY', locale: 'tr-TR' },
    blr: { currency: 'BYN', locale: 'be-BY' },
    bg: { currency: 'EUR', locale: 'bg-BG' },
    at: { currency: 'EUR', locale: 'de-AT' },
  };
  const cfg = currencyMap[regionCode] || currencyMap.ru;
  const zeroFractionCurrencies = new Set(['KZT', 'VND', 'UZS', 'JPY']);
  const useZero = zeroFractionCurrencies.has(cfg.currency);

  const getResolvedPrice = (product) => {
    const langKey = product.lang || 'ru';
    const price = product[`price_${regionCode}`] ?? product[`price_${langKey}`] ?? product.price_ru ?? product.price ?? null;
    return price !== null ? price : null;
  };

  const getResolvedPv = (product) => {
    const langKey = product.lang || 'ru';
    return product[`price_${regionCode}_pv`] ?? product[`price_${langKey}_pv`] ?? product.price_pv ?? product.price_ru_pv ?? null;
  };

  return (
    <>
      {isLoading && <Spinner />}

      {!isLoading && !isError && (
        <Swiper
          modules={[Navigation, Pagination]}
          navigation={{
            nextEl: `.${nextClassName}`,
            prevEl: `.${prevClassName}`,
          }}
          pagination={{
            el: `.swiper-pagination-${nextClassName}`,
            clickable: true,
          }}
          spaceBetween={20}
          slidesPerView={"auto"}
          className="recs__slider"
        >
          {careData.map((item, index) => {
            // Filter out products without regional price or with zero price
            const filteredProducts = (item.list_products || []).filter((p) => {
              const price = getResolvedPrice(p);
              return price !== null && Number(price) > 0;
            });
            if (filteredProducts.length > 0) {
              const product = filteredProducts[0];

              const hasAnalogInCart = filteredProducts.some(
                (analog) => analog.code !== product.code && cart[analog.code] > 0
              );

              const productCount = cart[product.code] || 0;
              const isLoadingProduct = loadingProducts[product.code];
              const isErrorProduct = errorProducts[product.code];
              const isSelectedAnalog = hasAnalogInCart;

              const langKey = product.lang || 'ru';
              const resolvedPrice = getResolvedPrice(product);
              const resolvedPv = getResolvedPv(product);
              // Currency/locale now derived from region configuration
              const priceFormat = (value) => Number(value).toLocaleString(cfg.locale, {
                style: 'currency',
                currency: cfg.currency,
                minimumFractionDigits: useZero ? 0 : 2,
                maximumFractionDigits: useZero ? 0 : 2,
              });

              return (
                <SwiperSlide
                  key={index}
                  className={`recs__card ${isSelectedAnalog ? "active" : ""}`}
                  data-card-id={product.code}
                >
                  {isLoadingProduct && <Spinner />}
                  {isErrorProduct && <ErrorScreen message={errorText} />}
                  {!isLoadingProduct && !isErrorProduct && (
                    <>
                      <span className="recs__stage">{t('recs.stage', 'Этап')} {index + 1}</span>
                      <img 
                        src={product.img} 
                        alt={product.name}
                        onClick={() => openProductModal(product)}
                        style={{ cursor: 'pointer' }}
                      />
                      <b>{product.stage_name.name}</b>
                      <p 
                        onClick={() => openProductModal(product)}
                        style={{ cursor: 'pointer' }}
                      >
                        {product.name}
                      </p>
                      {productCount === 0 && (
                        <button
                          className="btn btn--blue recs__quick-add"
                          onClick={() => handleAddToCart(product.code)}
                        >
                          {t('productCard.basket', 'В корзину')}
                        </button>
                      )}
                      {resolvedPrice !== null && (
                        <div className="recs__price">
                          {priceFormat(resolvedPrice)}
                          {resolvedPv !== null && (
                            <> | {Number(resolvedPv).toLocaleString(cfg.locale, { minimumFractionDigits: 0, maximumFractionDigits: 1 })} PV</>
                          )}
                        </div>
                      )}
                      <button
                        className={`btn ${
                          isSelectedAnalog ? "btn--pink" : "btn--transblue"
                        }`}
                        onClick={() => openAnalogsModal(filteredProducts)}
                      >
                        {t('productCard.analogs', 'Аналоги')}
                      </button>
                      {productCount > 0 ? (
                        <div className="recs__counter">
                          <button
                            className="btn btn--transblue"
                            onClick={() => handleRemoveFromCart(product.code)}
                          >
                            <span>-</span>
                          </button>
                          <p>{productCount}</p>
                          <button
                            className="btn btn--transblue"
                            onClick={() => handleAddToCart(product.code)}
                          >
                            <span>+</span>
                          </button>
                        </div>
                      ) : null}
                    </>
                  )}
                </SwiperSlide>
              );
            }
            return null;
          })}
        </Swiper>
      )}
      <button type="button"
        className={`swiper-button swiper-button-prev ${prevClassName}`}
      ></button>
      <button type="button"
        className={`swiper-button swiper-button-next ${nextClassName}`}
      ></button>
      <div
        className={`swiper-pagination swiper-pagination-${nextClassName}`}
      ></div>
    </>
  );
};

export default CareSection;
